import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  searchGeneratedTicketsApi,
  getTicketSignature,
} from "helpers/fakebackend_helper";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');
const RANGE_SPAN = 9;

const padLikeSource = (value: number, digitCount: number) =>
  String(Math.max(value, 0)).padStart(digitCount, "0");

const computeNumberTo = (from: string, maxNumberTo?: string | null) => {
  const digits = from.length;
  let end = parseInt(from, 10) + RANGE_SPAN;
  if (maxNumberTo && end > parseInt(maxNumberTo, 10)) {
    end = parseInt(maxNumberTo, 10);
  }
  return padLikeSource(end, digits);
};

const computeNumberFrom = (to: string) => {
  const digits = to.length;
  const start = parseInt(to, 10) - RANGE_SPAN;
  return padLikeSource(start, digits);
};

interface GeneratedTicket {
  ticketNumber: string;
  symbol: string;
}

interface SearchResult {
  matchedCount: number;
  tickets: GeneratedTicket[];
}

interface SearchByPrizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  lotteryPeriodId?: number | null;
  planCode?: string;
  expectedDrawDate?: string | null;
  // Dải số của bộ vé vừa sinh — dùng làm giá trị mặc định gợi ý, người dùng vẫn sửa được
  defaultNumberFrom?: string | null;
  defaultNumberTo?: string | null;
  // Danh sách ký hiệu vé của kỳ này (VD: ["A","B","C",...]) — mỗi ký hiệu lặp lại toàn bộ dải số
  // nên bắt buộc phải chọn để thu hẹp kết quả khi kỳ có hàng chục triệu vé
  ticketSymbols?: string[];
}

const MAX_DISPLAYED = 200;

const formatDrawDate = (date?: string | null) =>
  date ? moment(date).format("DD/MM/YYYY") : "-";

const SearchByPrizeModal: React.FC<SearchByPrizeModalProps> = ({
  isOpen,
  onClose,
  lotteryPeriodId,
  planCode,
  expectedDrawDate,
  defaultNumberFrom,
  defaultNumberTo,
  ticketSymbols = [],
}) => {
  const location = useLocation();
  const [numberFrom, setNumberFrom] = useState("");
  const [numberTo, setNumberTo] = useState("");
  const [fromTouched, setFromTouched] = useState(false);
  const [toTouched, setToTouched] = useState(false);
  const [symbol, setSymbol] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [lookupLoadingKey, setLookupLoadingKey] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<GeneratedTicket | null>(
    null,
  );
  const [selectedTicketQrValue, setSelectedTicketQrValue] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (isOpen) {
      setNumberFrom(defaultNumberFrom || "");
      setNumberTo(defaultNumberTo || "");
      setFromTouched(false);
      setToTouched(false);
      setSymbol(ticketSymbols[0] || "");
      setResult(null);
      setSelectedTicket(null);
      setSelectedTicketQrValue(null);
    }
  }, [isOpen, defaultNumberFrom, defaultNumberTo, ticketSymbols]);

  const handleNumberFromBlur = () => {
    const from = numberFrom.trim();
    if (from && !toTouched) {
      setNumberTo(computeNumberTo(from, defaultNumberTo));
    }
  };

  const handleNumberToBlur = () => {
    const to = numberTo.trim();
    if (!to) return;
    if (defaultNumberTo && parseInt(to, 10) > parseInt(defaultNumberTo, 10)) {
      setNumberTo(defaultNumberTo);
      if (!fromTouched) setNumberFrom(computeNumberFrom(defaultNumberTo));
      return;
    }
    if (!fromTouched) {
      setNumberFrom(computeNumberFrom(to));
    }
  };

  const handleSearch = async () => {
    if (!lotteryPeriodId) return;

    if (!numberFrom.trim() && !numberTo.trim()) {
      toast.error(st('vui_long_nhap_it_nhat_mot_message'));
      return;
    }

    setSearching(true);
    setResult(null);
    setSelectedTicket(null);
    setSelectedTicketQrValue(null);
    try {
      const response: any = await searchGeneratedTicketsApi(
        lotteryPeriodId,
        numberFrom.trim() || undefined,
        numberTo.trim() || undefined,
        symbol || undefined,
      );
      const data = response?.data || response;
      setResult({
        matchedCount: data.matchedCount || 0,
        tickets: (data.tickets || []).map((t: any) => ({
          ticketNumber: t.ticketNumber,
          symbol: t.symbol,
        })),
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        st('khong_the_tra_cuu_ve_text');
      toast.error(message);
    } finally {
      setSearching(false);
    }
  };

  const handleTicketClick = async (ticket: GeneratedTicket) => {
    if (!lotteryPeriodId) return;

    const key = `${ticket.ticketNumber}-${ticket.symbol}`;
    if (
      selectedTicket &&
      `${selectedTicket.ticketNumber}-${selectedTicket.symbol}` === key
    ) {
      setSelectedTicket(null);
      setSelectedTicketQrValue(null);
      return;
    }

    setLookupLoadingKey(key);
    try {
      const response: any = await getTicketSignature(
        lotteryPeriodId,
        ticket.ticketNumber,
        ticket.symbol,
      );
      const payload = response?.data || response;
      const token = payload?.token;

      if (!token) {
        toast.error(st('khong_lay_duoc_ma_tra_cuu_message'));
        return;
      }

      const qrValue = `${window.location.origin}/v/${encodeURIComponent(token)}`;
      setSelectedTicket(ticket);
      setSelectedTicketQrValue(qrValue);
    } catch (error: any) {
      const message =
        error?.data?.message ||
        error?.message ||
        st('khong_the_lay_thong_tin_tra_text');
      toast.error(message);
    } finally {
      setLookupLoadingKey(null);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <i className="ri-search-line align-bottom me-2"></i>
        {st('tra_cuu_ve_so_vua_tao_title')}{planCode ? st('ky_plan_code_text', { plan_code: planCode }) : ""}
      </ModalHeader>
      <ModalBody>
        {expectedDrawDate && (
          <div className="mb-2 text-muted">
            {st('ngay_mo_thuong_text')} <strong>{formatDrawDate(expectedDrawDate)}</strong>
          </div>
        )}
        <div className="row g-3 mb-2">
          <div className="col-md-3">
            <FormGroup className="mb-0">
              <Label>{st('ky_hieu_ve_label')}</Label>
              <Input
                type="select"
                value={symbol}
                data-action-code="input-symbol"
                data-action-name="Ký hiệu vé (tra cứu)"
                disabled={
                  !PageActionPermission.canEnable(location.pathname, "input-symbol")
                }
                onChange={(e) => setSymbol(e.target.value)}
              >
                <option value="">{st('tat_ca_ky_hieu_option')}</option>
                {ticketSymbols.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-4">
            <FormGroup className="mb-0">
              <Label>{st('tu_so_ve_label')}</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder={st('vd_000123_placeholder')}
                value={numberFrom}
                data-action-code="input-number-from"
                data-action-name="Từ số vé"
                disabled={
                  !PageActionPermission.canEnable(location.pathname, "input-number-from")
                }
                onChange={(e) => {
                  setNumberFrom(e.target.value.replace(/\D/g, ""));
                  setFromTouched(true);
                }}
                onBlur={handleNumberFromBlur}
              />
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup className="mb-0">
              <Label>{st('den_so_ve_label')}</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder={st('vd_000456_placeholder')}
                value={numberTo}
                data-action-code="input-number-to"
                data-action-name="Đến số vé"
                disabled={
                  !PageActionPermission.canEnable(location.pathname, "input-number-to")
                }
                onChange={(e) => {
                  setNumberTo(e.target.value.replace(/\D/g, ""));
                  setToTouched(true);
                }}
                onBlur={handleNumberToBlur}
              />
            </FormGroup>
          </div>
          <div className="col-md-2">
            <Label className="invisible">&nbsp;</Label>
            {PageActionPermission.canDisplay(location.pathname, "button-search") && (
              <Button
                color="primary"
                className="w-100"
                data-action-code="button-search"
                data-action-name="Tra cứu vé"
                onClick={handleSearch}
                disabled={searching}
              >
                {searching ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  <i className="ri-search-line"></i>
                )}
              </Button>
            )}
          </div>
        </div>
        <div className="form-text mb-3">
          <i className="ri-information-line me-1"></i>
          {st('chon_ky_hieu_de_thu_hep_text')}
        </div>

        {result && (
          <>
            <div className="mb-2 text-muted">
              {st('tim_thay_text')}{" "}
              <strong>{result.matchedCount.toLocaleString("vi-VN")}</strong> {st('ve_text')}
              {result.matchedCount > result.tickets.length && (
                <span> {st('hien_thi_text')} {result.tickets.length} {st('ve_dau_tien_text')}</span>
              )}
            </div>
            {result.tickets.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="ri-inbox-line" style={{ fontSize: "36px" }}></i>
                <p className="mt-2 mb-0">
                  {st('khong_tim_thay_ve_nao_trong_text')}
                </p>
              </div>
            ) : (
              <div
                className="d-flex flex-wrap gap-2"
                style={{ maxHeight: 320, overflowY: "auto" }}
              >
                {result.tickets.slice(0, MAX_DISPLAYED).map((t) => {
                  const key = `${t.ticketNumber}-${t.symbol}`;
                  const isLoading = lookupLoadingKey === key;
                  const isSelected =
                    selectedTicket &&
                    `${selectedTicket.ticketNumber}-${selectedTicket.symbol}` ===
                      key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`btn border-0 fs-6 ${isSelected ? "bg-success text-white" : "bg-success-subtle text-success"}`}
                      style={{ width: 130, flex: "0 0 auto" }}
                      disabled={isLoading}
                      title={st('xem_ma_ve_ky_phat_hanh_title')}
                      onClick={() => handleTicketClick(t)}
                    >
                      {isLoading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                      ) : (
                        <>
                          {t.ticketNumber} - {t.symbol}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {selectedTicket && selectedTicketQrValue && (
              <div className="border rounded p-3 mt-3 d-flex align-items-center gap-3">
                <QRCodeCanvas value={selectedTicketQrValue} size={120} />
                <div>
                  <div className="fw-semibold fs-5">
                    {selectedTicket.ticketNumber} - {selectedTicket.symbol}
                  </div>
                  <div className="text-muted">
                    {st('ky_phat_hanh_text')} {planCode || "—"}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          {st('dong_button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SearchByPrizeModal;
